import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DELIVERY_COCKPIT_TOUR_DISMISSED_KEY,
    DELIVERY_COCKPIT_TOUR_AUTO_OPENED_KEY,
    DELIVERY_COCKPIT_TOUR_VERSION,
    DELIVERY_COCKPIT_TOUR_PROGRESS_PREFIX,
    resolveDeliveryCockpitTourPersona,
    getTourIdForPersona,
} from '../constants/deliveryCockpitTourPersonas';
import { getTourStepsForPersona } from '../constants/deliveryCockpitTourGraphs';
import {
    canAdvanceTourStep,
    matchTourRoute,
    useTourRouteMatch,
} from './useTourRouteMatch';

/** Suppress auto-open for other roles in the same browser session after first show/dismiss. */
export const DELIVERY_COCKPIT_TOUR_SESSION_KEY = 'delivery_cockpit_tour_session_seen';

/**
 * @param {number|string|null|undefined} userId
 * @returns {string|null}
 */
const buildDismissedKey = (userId) => {
    if (userId === null || userId === undefined || userId === '') {
        return null;
    }
    return `${DELIVERY_COCKPIT_TOUR_DISMISSED_KEY}:${DELIVERY_COCKPIT_TOUR_VERSION}:${userId}`;
};

/**
 * @param {number|string|null|undefined} userId
 * @returns {string|null}
 */
const buildAutoOpenedKey = (userId) => {
    if (userId === null || userId === undefined || userId === '') {
        return null;
    }
    return `${DELIVERY_COCKPIT_TOUR_AUTO_OPENED_KEY}:${DELIVERY_COCKPIT_TOUR_VERSION}:${userId}`;
};

/**
 * @param {number|string|null|undefined} userId
 * @param {string} persona
 * @returns {string|null}
 */
export const buildTourProgressKey = (userId, persona) => {
    if (!userId || !persona) {
        return null;
    }
    return `${DELIVERY_COCKPIT_TOUR_PROGRESS_PREFIX}:${DELIVERY_COCKPIT_TOUR_VERSION}:${userId}:${persona}`;
};

/**
 * @param {string|null} key
 * @returns {object|null}
 */
const readTourProgress = (key) => {
    if (!key || typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/**
 * @param {string|null} key
 * @param {object} value
 */
const writeTourProgress = (key, value) => {
    if (!key || typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Ignore quota errors.
    }
};

/**
 * @param {string|null} storageKey
 * @returns {boolean}
 */
const readDismissed = (storageKey) => {
    if (!storageKey || typeof window === 'undefined') {
        return false;
    }
    try {
        if (window.sessionStorage.getItem(DELIVERY_COCKPIT_TOUR_SESSION_KEY) === '1') {
            return true;
        }
        return window.localStorage.getItem(storageKey) === '1';
    } catch {
        return false;
    }
};

export const markDeliveryCockpitTourSessionSeen = () => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.setItem(DELIVERY_COCKPIT_TOUR_SESSION_KEY, '1');
    } catch {
        // Ignore.
    }
};

export const markDeliveryCockpitTourAutoOpened = (userId) => {
    const key = buildAutoOpenedKey(userId);
    if (!key || typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(key, '1');
    } catch {
        // Ignore.
    }
};

export const persistDeliveryCockpitTourDismissed = (userId, dismissed) => {
    const key = buildDismissedKey(userId);
    if (!key || typeof window === 'undefined') {
        return;
    }
    try {
        if (dismissed) {
            window.localStorage.setItem(key, '1');
        } else {
            window.localStorage.removeItem(key);
        }
    } catch {
        // Ignore.
    }
};

/**
 * Guided tour state: progress, completion detection, persistence.
 *
 * @param {object} options
 * @param {number|string|null|undefined} options.userId
 * @param {string|null|undefined} options.role
 * @param {boolean} [options.is_superuser]
 * @param {string} [options.firstName]
 * @param {number} [options.pendingApprovalCount]
 * @param {boolean} [options.isOpen]
 */
export const useDeliveryCockpitTour = ({
    userId,
    role,
    is_superuser: isSuperuser,
    firstName,
    pendingApprovalCount,
    isOpen,
} = {}) => {
    const persona = useMemo(
        () => resolveDeliveryCockpitTourPersona(role, { is_superuser: isSuperuser }),
        [role, isSuperuser],
    );

    const tourId = useMemo(() => getTourIdForPersona(persona), [persona]);

    const steps = useMemo(
        () => getTourStepsForPersona(persona, { firstName, pendingApprovalCount }),
        [persona, firstName, pendingApprovalCount],
    );

    const progressKey = useMemo(
        () => buildTourProgressKey(userId, persona),
        [userId, persona],
    );

    const dismissedKey = useMemo(() => buildDismissedKey(userId), [userId]);

    const [dismissed, setDismissed] = useState(() => readDismissed(dismissedKey));

    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [markedDoneIds, setMarkedDoneIds] = useState([]);
    const [skippedStepIds, setSkippedStepIds] = useState([]);
    const [isMinimized, setIsMinimized] = useState(false);

    const currentStep = steps[activeStepIndex] ?? steps[0];
    const routeMatched = useTourRouteMatch(currentStep?.route);

    const stepMarkedDone = markedDoneIds.includes(currentStep?.id);
    const stepSkipped = skippedStepIds.includes(currentStep?.id);

    const canGoNext = canAdvanceTourStep(
        currentStep,
        routeMatched,
        stepMarkedDone,
        stepSkipped,
    );

    const isLastStep = activeStepIndex >= steps.length - 1;

    const restoreProgress = useCallback(() => {
        const saved = readTourProgress(progressKey);
        if (!saved?.lastStepId) {
            setActiveStepIndex(0);
            setMarkedDoneIds(saved?.completedStepIds || []);
            return;
        }
        const idx = steps.findIndex((s) => s.id === saved.lastStepId);
        setActiveStepIndex(idx >= 0 ? idx : 0);
        setMarkedDoneIds(saved.completedStepIds || []);
    }, [progressKey, steps]);

    const persistProgress = useCallback(() => {
        writeTourProgress(progressKey, {
            lastStepId: currentStep?.id,
            completedStepIds: markedDoneIds,
            updatedAt: new Date().toISOString(),
        });
    }, [progressKey, currentStep?.id, markedDoneIds]);

    useEffect(() => {
        if (isOpen) {
            persistProgress();
        }
    }, [isOpen, activeStepIndex, markedDoneIds, persistProgress]);

    const dismissTour = useCallback(() => {
        setDismissed(true);
        markDeliveryCockpitTourSessionSeen();
        persistDeliveryCockpitTourDismissed(userId, true);
        writeTourProgress(progressKey, {
            lastStepId: currentStep?.id,
            completedStepIds: markedDoneIds,
            dismissedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
    }, [userId, progressKey, currentStep?.id, markedDoneIds]);

    /**
     * Hide the tour for this browser session only — still eligible on next login.
     */
    const suppressTourForSession = useCallback(() => {
        markDeliveryCockpitTourSessionSeen();
        writeTourProgress(progressKey, {
            lastStepId: currentStep?.id,
            completedStepIds: markedDoneIds,
            updatedAt: new Date().toISOString(),
        });
    }, [progressKey, currentStep?.id, markedDoneIds]);

    const resetDismissal = useCallback(() => {
        setDismissed(false);
        persistDeliveryCockpitTourDismissed(userId, false);
    }, [userId]);

    const markAutoOpened = useCallback(() => {
        // Legacy key retained for older clients; next-login suppress uses dismissTour only.
        markDeliveryCockpitTourAutoOpened(userId);
    }, [userId]);

    const startFullTour = useCallback(() => {
        setActiveStepIndex(0);
        setMarkedDoneIds([]);
        setSkippedStepIds([]);
        setIsMinimized(false);
        resetDismissal();
    }, [resetDismissal]);

    const resumeTour = useCallback(() => {
        restoreProgress();
        setIsMinimized(false);
        resetDismissal();
    }, [restoreProgress, resetDismissal]);

    const restartTour = useCallback(() => {
        if (progressKey && typeof window !== 'undefined') {
            try {
                window.localStorage.removeItem(progressKey);
            } catch {
                // Ignore.
            }
        }
        startFullTour();
    }, [progressKey, startFullTour]);

    const goNext = useCallback(() => {
        if (!canGoNext || isLastStep) {
            return;
        }
        const nextId = currentStep?.id;
        setMarkedDoneIds((prev) => (nextId && !prev.includes(nextId) ? [...prev, nextId] : prev));
        setActiveStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, [canGoNext, isLastStep, currentStep?.id, steps.length]);

    const goBack = useCallback(() => {
        setActiveStepIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const markStepDone = useCallback(() => {
        const id = currentStep?.id;
        if (!id) {
            return;
        }
        setMarkedDoneIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, [currentStep?.id]);

    const skipStep = useCallback(() => {
        const id = currentStep?.id;
        if (!id) {
            return;
        }
        setSkippedStepIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, [currentStep?.id]);

    const hasSavedProgress = useMemo(() => {
        const saved = readTourProgress(progressKey);
        return Boolean(saved?.lastStepId && saved.lastStepId !== 'welcome');
    }, [progressKey, isOpen]);

    // Permanent suppress is only via dismissTour (checkbox). autoOpened is retained for
    // analytics / legacy keys but does not block next-login auto-open.
    const shouldAutoOpen = Boolean(userId) && !dismissed;

    return {
        persona,
        tourId,
        steps,
        activeStepIndex,
        currentStep,
        stepCount: steps.length,
        routeMatched,
        canGoNext,
        isLastStep,
        isMinimized,
        setIsMinimized,
        shouldAutoOpen,
        hasSavedProgress,
        dismissed,
        dismissTour,
        suppressTourForSession,
        resetDismissal,
        markAutoOpened,
        startFullTour,
        resumeTour,
        restartTour,
        goNext,
        goBack,
        markStepDone,
        skipStep,
        restoreProgress,
    };
};

export { matchTourRoute };
